import {
  ArrowRight,
  Building2,
  CarFront,
  CarTaxiFront,
  CircleAlert,
  Clock3,
  FileClock,
  FileSignature,
  HeartPulse,
  Plus,
  TrendingUp,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge, Card, EmptyState, Skeleton } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { useCustomers, useDashboardData } from "../data/platform-data";
import { useAssetDashboard } from "../data/assets-platform-data";
import { useTenantRuntime } from "../config/TenantRuntime";
import { env } from "../lib/env";
import { resetDemoSession } from "../lib/demo-session";
import { useTeamData } from "../hooks/usePlatformData";
import type { TeamListRow } from "../repositories/platform";
import { useEffect, useRef, useState } from "react";

export function countActiveSellers(rows: readonly TeamListRow[]) {
  return rows.filter(
    (member) =>
      member.status === "ATIVO" && member.role.toLocaleLowerCase("pt-BR").includes("vendedor"),
  ).length;
}

function tone(status: string): "success" | "warning" | "info" | "danger" {
  if (status === "ACTIVE" || status === "ATIVO") return "success";
  if (status === "OVERDUE" || status === "VENCIDO") return "danger";
  if (status.includes("PAYMENT") || status.includes("PAGAMENTO")) return "warning";
  return "info";
}

function AssetDashboard() {
  const dashboard = useAssetDashboard();
  if (dashboard.isLoading) return <Skeleton className="dashboard-skeleton" />;
  if (dashboard.error) {
    return (
      <EmptyState
        title="Não foi possível carregar o patrimônio"
        description={dashboard.error.message}
      />
    );
  }
  const data = dashboard.data ?? { metrics: [], sales: [], receivables: [] };

  return (
    <div className="page-stack">
      <div className="notice">
        <CircleAlert size={20} />
        <div>
          <strong>Produto Patrimônio e Recebíveis</strong>
          <p>
            Veículos, imóveis, locações, vendas parceladas e segunda via usam o mesmo motor
            financeiro da Yatta.
          </p>
        </div>
      </div>

      <section className="metric-grid">
        {data.metrics.map((metric) => (
          <article className="metric-card" key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
            <small>{metric.delta}</small>
          </article>
        ))}
      </section>

      <div className="three-feature-grid">
        <Link className="feature-navigation-card" to="/vendas-veiculos">
          <CarFront size={23} />
          <div>
            <strong>Venda de veículos</strong>
            <span>À vista e parcelada</span>
          </div>
          <ArrowRight size={17} />
        </Link>
        <Link className="feature-navigation-card" to="/locacoes-imoveis">
          <Building2 size={23} />
          <div>
            <strong>Locação de imóveis</strong>
            <span>40 contratos estimados</span>
          </div>
          <ArrowRight size={17} />
        </Link>
        <Link className="feature-navigation-card" to="/locacoes-veiculos">
          <CarTaxiFront size={23} />
          <div>
            <strong>Locação para motoristas</strong>
            <span>30 veículos estimados</span>
          </div>
          <ArrowRight size={17} />
        </Link>
      </div>

      <div className="two-column">
        <Card
          title="Vendas parceladas recentes"
          description="Contratos, saldos e situação da transferência"
          action={
            <Link className="text-link" to="/vendas-veiculos">
              Ver todas <ArrowRight size={15} />
            </Link>
          }
        >
          <DataTable
            rows={data.sales}
            getKey={(sale) => sale.id}
            columns={[
              { key: "customer", header: "Cliente", render: (sale) => sale.customer },
              { key: "vehicle", header: "Veículo", render: (sale) => sale.vehicle },
              {
                key: "status",
                header: "Status",
                render: (sale) => <Badge tone={tone(sale.status)}>{sale.status}</Badge>,
              },
              { key: "total", header: "Venda", render: (sale) => sale.total },
            ]}
          />
        </Card>

        <Card title="Atenções operacionais" description="Fila priorizada">
          <div className="task-list">
            <div className="task-item">
              <FileClock size={18} />
              <div>
                <strong>
                  {data.receivables.filter((item) => item.status === "OVERDUE").length} parcelas
                  vencidas
                </strong>
                <span>Valores atualizados para segunda via</span>
              </div>
            </div>
            <div className="task-item">
              <Clock3 size={18} />
              <div>
                <strong>12 transferências pendentes</strong>
                <span>Vendas de veículos ainda em processamento</span>
              </div>
            </div>
            <div className="task-item">
              <TrendingUp size={18} />
              <div>
                <strong>Portal de segunda via preparado</strong>
                <span>CPF/CNPJ + OTP + Pix ou boleto</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function PlansDashboard() {
  const data = useDashboardData();
  const customers = useCustomers();
  const team = useTeamData();
  const [resetRequested, setResetRequested] = useState(false);
  const cancelResetRef = useRef<HTMLButtonElement>(null);
  const confirmResetRef = useRef<HTMLButtonElement>(null);
  const openResetRef = useRef<HTMLButtonElement>(null);
  const wasResetOpen = useRef(false);

  useEffect(() => {
    if (!resetRequested) {
      if (wasResetOpen.current) openResetRef.current?.focus();
      wasResetOpen.current = false;
      return;
    }
    wasResetOpen.current = true;
    cancelResetRef.current?.focus();
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setResetRequested(false);
      if (event.key === "Tab") {
        event.preventDefault();
        if (document.activeElement === cancelResetRef.current) confirmResetRef.current?.focus();
        else cancelResetRef.current?.focus();
      }
    };
    window.addEventListener("keydown", closeWithEscape);
    return () => window.removeEventListener("keydown", closeWithEscape);
  }, [resetRequested]);
  if (data.loading || customers.isLoading || team.isLoading)
    return <Skeleton className="dashboard-skeleton" />;
  if (data.error || customers.error || team.error) {
    return (
      <EmptyState
        title="Não foi possível carregar o painel"
        description={(data.error ?? customers.error ?? team.error)?.message ?? "Tente novamente."}
      />
    );
  }

  const customerRows = customers.data ?? [];
  const attentionRows = data.sales.slice(0, 4).map((sale, index) => ({
    id: sale.id,
    customer: sale.customer,
    issue:
      sale.status.includes("PAGAMENTO") || sale.status.includes("PAYMENT")
        ? "Pagamento pendente"
        : index % 2 === 0
          ? "Cadastro em revisão"
          : "Acesso pendente ao sistema",
    owner: sale.seller,
  }));
  const latestCustomers = customerRows.slice(0, 4);
  const pendingCount = attentionRows.length;
  const activeSellers = countActiveSellers(team.data ?? []);
  const metrics = [
    { label: "Clientes cadastrados", value: customerRows.length },
    { label: "Vendedores ativos", value: activeSellers },
    { label: "Pendências", value: pendingCount },
  ];
  const today = new Intl.DateTimeFormat("pt-BR", { dateStyle: "full" }).format(new Date());

  return (
    <div className="page-stack">
      <section className="dashboard-hero">
        <div>
          <p className="eyebrow">CRUZEIRO LIFE</p>
          <h2>Início</h2>
          <p>Organize os cadastros e acompanhe sua equipe.</p>
        </div>
        <Link className="button button-primary dashboard-primary-action" to="/clientes/novo">
          <Plus size={18} /> Cadastrar cliente
        </Link>
      </section>
      <div className="dashboard-date">{today}</div>
      <section className="dashboard-metrics" aria-label="Indicadores operacionais">
        {metrics.map((metric) => (
          <article className="dashboard-metric" key={metric.label}>
            <strong>{metric.value}</strong>
            <span>{metric.label}</span>
          </article>
        ))}
      </section>
      {env.useMocks && (
        <section className="dashboard-demo-guide" aria-label="Percurso da demonstração">
          <div>
            <strong>Quer explorar a demonstração?</strong>
            <span>Comece por um cadastro e acompanhe os fluxos da equipe.</span>
          </div>
          <nav aria-label="Atalhos da demonstração">
            <Link to="/clientes">
              <Users size={16} /> Abrir clientes
            </Link>
            <Link to="/equipe">
              <Users size={16} /> Abrir equipe
            </Link>
            <Link to="/contratos">
              <FileSignature size={16} /> Abrir contratos
            </Link>
            <Link to="/telemedicina">
              <HeartPulse size={16} /> Abrir telemedicina
            </Link>
          </nav>
        </section>
      )}
      <section className="dashboard-section">
        <div className="section-heading">
          <h2>Precisa de atenção</h2>
          <Link className="text-link" to="/clientes">
            Ver clientes <ArrowRight size={15} />
          </Link>
        </div>
        {attentionRows.length === 0 ? (
          <EmptyState
            title="Operação em dia"
            description="Não há pendências para a equipe neste momento."
          />
        ) : (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Pendência</th>
                  <th>Responsável</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {attentionRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.customer}</strong>
                    </td>
                    <td>{row.issue}</td>
                    <td>{row.owner}</td>
                    <td>
                      <Link className="table-action" to="/clientes">
                        Abrir clientes
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="dashboard-section">
        <div className="section-heading">
          <h2>Últimos cadastros</h2>
          <Link className="text-link" to="/clientes">
            Ver todos <ArrowRight size={15} />
          </Link>
        </div>
        {latestCustomers.length === 0 ? (
          <EmptyState
            title="Nenhum cliente cadastrado"
            description="Comece cadastrando o primeiro cliente da equipe."
          />
        ) : (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Origem</th>
                  <th>Vendedor responsável</th>
                  <th>Cadastro</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {latestCustomers.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <strong>{customer.holder || customer.name}</strong>
                    </td>
                    <td>
                      {env.useMocks ? "Demonstração · origem sintética" : "Informada no cadastro"}
                    </td>
                    <td>{customer.seller}</td>
                    <td>{env.useMocks ? "22/09/2026" : "—"}</td>
                    <td>
                      <Badge tone={customer.balance === "Vencido" ? "warning" : "success"}>
                        {customer.balance === "Vencido" ? "Dados pendentes" : "Completo"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {env.useMocks && (
        <section className="demo-reset-panel" aria-label="Controle da demonstração">
          <div>
            <p className="eyebrow">AMBIENTE DE HOMOLOGAÇÃO</p>
            <h2>Dados fictícios para apresentação</h2>
            <p>
              Reinicie a sessão para remover cadastros, vendedores e contratos criados durante o
              teste.
            </p>
          </div>
          {!resetRequested ? (
            <button
              ref={openResetRef}
              className="button button-secondary"
              type="button"
              onClick={() => setResetRequested(true)}
            >
              Reiniciar demonstração
            </button>
          ) : (
            <div
              className="demo-reset-confirm"
              role="dialog"
              aria-label="Confirmar reinício da demonstração"
              aria-modal="true"
              aria-describedby="demo-reset-description"
            >
              <strong id="demo-reset-title">Remover dados criados nesta sessão?</strong>
              <span id="demo-reset-description">
                A ação limpa somente dados fake locais e não altera dados externos.
              </span>
              <div className="card-actions">
                <button
                  ref={cancelResetRef}
                  className="button button-secondary"
                  type="button"
                  onClick={() => setResetRequested(false)}
                >
                  Cancelar
                </button>
                <button
                  ref={confirmResetRef}
                  className="button button-danger"
                  type="button"
                  onClick={() => {
                    resetDemoSession();
                    setResetRequested(false);
                  }}
                >
                  Confirmar reinício
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export function DashboardPage() {
  const { hasModule } = useTenantRuntime();
  return hasModule("assets") ? <AssetDashboard /> : <PlansDashboard />;
}
